// Central content + mock data for TaylorMade Technology Advisors.
// All figures sourced from the client's Website Blueprint.

import {
  Radar, Server, ShieldCheck, Network, Headphones, Cpu, Wrench,
  TrendingUp, Clock, FileWarning, UserCog, Handshake, Rocket,
} from "lucide-react";

export const COMPANY = {
  name: "TaylorMade Technology Advisors",
  short: "TaylorMade Tech",
  tagline: "Drive Success While You Scale",
  phone: "(682) 422-6946",
  phoneHref: "tel:+16824226946",
  email: "hello@taylormadetechnologyadvisors.com",
  address: "564 N Walnut Creek Dr, Ste 100, Mansfield, Texas 76063",
};

export const HERO = {
  kicker: "Independent • Buyer-Side • Supplier-Neutral",
  headline: "Your IT vendors are selling you something.",
  headlineAccent: "We're not.",
  sub:
    "Every vendor pitch, funded report, and comp'd analyst has a stake in your decision. We're the independent advocate on your side of the table — so you buy technology without regret.",
};

export const HERO_STATS = [
  { value: "96", label: "Net Promoter Score", note: "EY-Parthenon validated" },
  { value: "24%", label: "Average Savings", note: "across engagements" },
  { value: "$300M+", label: "Advisory Spend", note: "under management" },
  { value: "600+", label: "Vetted Providers", note: "technology & telecom" },
];

// The "Smart Teams Get Burned" risk matrix
export const RISK_CARDS = [
  {
    title: "The Analyst Report",
    problem: "You trust the 'independent' quadrant.",
    reality: "It's funded by the vendors ranked inside it.",
    consequence: "You anchor on marketing budgets, not fit.",
  },
  {
    title: "The Vendor Rep",
    problem: "You lean on your account manager for advice.",
    reality: "Their commission depends on what you buy.",
    consequence: "You get the deal that's best for them.",
  },
  {
    title: "The Renewal",
    problem: "You auto-renew to avoid disruption.",
    reality: "Price creep and dead clauses go unchecked.",
    consequence: "You overpay 20–40% on autopilot.",
  },
];

// With / Without split matrix
export const WITHOUT_US = [
  "Vendor-funded 'research' guides your shortlist",
  "One-sided contracts with no exit ramps",
  "Renewals negotiated from zero leverage",
  "Implementation risk lands on your team",
  "No one accountable after the ink dries",
];

export const WITH_US = [
  "Independent, benchmarked market scan across 600+ providers",
  "Contracts pressure-tested for price caps & exit clauses",
  "Negotiation from a price-floor baseline",
  "Project management that protects your bandwidth",
  "Life-of-contract accountability & quarterly reviews",
];

export const NEUTRALITY = {
  kicker: "The Neutrality Model",
  title: "We're paid the same, whoever you pick.",
  body:
    "Suppliers pay us a standardized rate — so our compensation is neutral to which provider you choose. No kickbacks for steering you, no bonus for the biggest logo. Our only incentive is the outcome that keeps you as a client for life.",
  points: [
    { label: "100% Provider Agnostic", desc: "No reseller quotas. No preferred-vendor pressure." },
    { label: "Standardized Compensation", desc: "The same rate regardless of your selection." },
    { label: "Buyer-Side Only", desc: "We never represent the supplier's interests." },
  ],
};

export const STAKEHOLDERS = [
  {
    role: "Technology Leaders",
    icon: UserCog,
    value: "Cut through vendor noise with benchmarked, apples-to-apples evaluations — and reclaim your team's bandwidth.",
  },
  {
    role: "Finance Leaders",
    icon: TrendingUp,
    value: "Turn opaque tech spend into modeled TCO, hard savings, and clean, defensible contracts.",
  },
  {
    role: "Business Leaders",
    icon: Rocket,
    value: "Make technology a driver of scale — not the reason a critical initiative stalls or fails.",
  },
  {
    role: "Boards & Investors",
    icon: Handshake,
    value: "De-risk the tech thesis with independent diligence, TCO modeling, and post-close accountability.",
  },
];

// 6-step process
export const PROCESS = [
  {
    step: "01",
    title: "Discovery & Needs",
    desc: "Stakeholder interviews to surface the root causes of IT friction — not just the symptoms.",
  },
  {
    step: "02",
    title: "Define Requirements",
    desc: "Budget development and explicit, measurable success metrics agreed up front.",
  },
  {
    step: "03",
    title: "Solution Evaluation",
    desc: "We scan the full landscape — beyond the firms with the biggest marketing budgets.",
  },
  {
    step: "04",
    title: "Sourcing & Negotiation",
    desc: "Side-by-side matrices and price-floor benchmarking to negotiate from real leverage.",
  },
  {
    step: "05",
    title: "Implementation",
    desc: "Hands-on project management that protects your internal resource bandwidth.",
  },
  {
    step: "06",
    title: "Life-of-Contract",
    desc: "Post-sale escalations and quarterly reviews. We stay accountable long after the signature.",
  },
];

export const PROCESS_STATS = [
  { value: "48hr", label: "Typical ClarityMap turnaround" },
  { value: "21,500+", label: "Technology solutions researched" },
  { value: "50%", label: "Reduction in procurement time" },
];

// Solutions / practice areas
export const PRACTICES = [
  {
    slug: "cybersecurity",
    name: "Cybersecurity & Resilience",
    tool: "Vigilance Verification™",
    icon: ShieldCheck,
    desc: "Sourcing MDR, SASE, and compliance tooling that stands up to real threats and auditors alike.",
  },
  {
    slug: "cloud",
    name: "Infrastructure & Cloud",
    tool: "Infrastructure Insight™",
    icon: Server,
    desc: "Multitenant, private, hybrid, and public environments — right-sized to your workloads and budget.",
  },
  {
    slug: "managed-services",
    name: "Managed Services",
    tool: "MSP Microscope™",
    icon: Wrench,
    desc: "Evaluate hybrid footprints and outsourced IT partners against what actually gets delivered.",
  },
  {
    slug: "connectivity",
    name: "Connectivity & Network",
    tool: "Network Pulse™",
    icon: Network,
    desc: "Sourcing WAN, SD-WAN, and circuits up to 100 Gbps with benchmarked pricing.",
  },
  {
    slug: "customer-experience",
    name: "Customer Experience",
    tool: "Experience Examination™",
    icon: Headphones,
    desc: "Cloud CCaaS / UCaaS to retire legacy PBX and modernize every customer touchpoint.",
  },
  {
    slug: "ai",
    name: "AI Sourcing & Enablement",
    tool: "AI Readiness Radar™",
    icon: Cpu,
    desc: "Practical AI integration focused on real use cases and measurable ROI — not the hype cycle.",
  },
];

// Situational triggers
export const TRIGGERS = [
  {
    id: "scaling",
    title: "Scaling Fast",
    icon: TrendingUp,
    summary: "Headcount is growing and your contracts and stack need resizing — fast.",
    checklist: [
      "Right-size seat-based licensing before you re-up",
      "Renegotiate volume tiers ahead of the growth curve",
      "Stress-test network capacity for new locations",
    ],
  },
  {
    id: "compliance",
    title: "Compliance Deadline",
    icon: ShieldCheck,
    summary: "An urgent SOC 2, HIPAA, or security requirement just landed on your desk.",
    checklist: [
      "Map controls to the framework you're being held to",
      "Source tooling that satisfies auditors, not just checkboxes",
      "Document evidence workflows before the deadline",
    ],
  },
  {
    id: "renewal",
    title: "Contract Renewal",
    icon: Clock,
    summary: "A multi-year deal is ending and needs real benchmarking before you sign again.",
    checklist: [
      "Benchmark current pricing against the market floor",
      "Hunt for missing exit ramps and price caps",
      "Time the negotiation to your leverage, not theirs",
    ],
  },
  {
    id: "inherited",
    title: "Inherited Stack",
    icon: FileWarning,
    summary: "A new CTO or CFO just inherited a pile of unvetted legacy contracts.",
    checklist: [
      "Inventory every active agreement and auto-renewal",
      "Flag the traps: evergreen clauses, hidden overages",
      "Build a rationalization roadmap with quick wins first",
    ],
  },
  {
    id: "customer",
    title: "Customer Requirement",
    icon: Handshake,
    summary: "A deal is on the line — a customer needs vendor credentials to close.",
    checklist: [
      "Identify exactly which attestations unlock the deal",
      "Source the fastest compliant path to 'yes'",
      "Package proof your customer's procurement will accept",
    ],
  },
  {
    id: "transformation",
    title: "Digital Transformation",
    icon: Rocket,
    summary: "You're migrating to public cloud or SD-WAN and can't afford a misstep.",
    checklist: [
      "Model TCO across migration scenarios",
      "Sequence the cutover to minimize business risk",
      "Lock accountability into the implementation SOW",
    ],
  },
];

export const INDUSTRIES = ["Healthcare", "Financial Services", "Manufacturing", "Multi-location QSR / Retail"];

// About / team
export const TEAM = [
  {
    name: "Marcus Taylor",
    title: "Founding Partner — Former CIO",
    bio: "Two decades leading enterprise IT. Sat in the buyer's chair for hundreds of vendor negotiations.",
    initials: "MT",
  },
  {
    name: "Priya Nadeau",
    title: "Partner — Cloud & Infrastructure",
    bio: "Former carrier-side solutions engineer. Knows exactly how the sausage — and the pricing — gets made.",
    initials: "PN",
  },
  {
    name: "David Ellison",
    title: "Partner — Cybersecurity & Compliance",
    bio: "Ex-CISO who has passed the audits and cleaned up after the breaches on both sides of the table.",
    initials: "DE",
  },
  {
    name: "Sofia Reyes",
    title: "Partner — Finance & TEM",
    bio: "Finance leader specializing in TCO modeling and turning invoice chaos into defensible savings.",
    initials: "SR",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "They found leverage in our renewal we didn't know existed — and stayed in the room until it landed. This isn't a broker; it's an extension of my team.",
    name: "VP of IT",
    org: "National Healthcare System",
  },
  {
    quote:
      "For the first time, I had an apples-to-apples view of the market that wasn't written by a vendor. We cut 27% and signed a contract I can actually defend to the board.",
    name: "CIO",
    org: "Financial Services Firm",
  },
  {
    quote:
      "The billing audit paid for the entire engagement in the first month. They read the receipts nobody else bothered to.",
    name: "CFO",
    org: "Multi-location Retail Group",
  },
];

export const WHY_US = [
  { title: "Advisor for Life", desc: "We measure success in multi-decade retention, not closed transactions." },
  { title: "Former CxOs & Carrier Engineers", desc: "The people advising you have sat in your chair — and theirs." },
  { title: "100% Provider Agnostic", desc: "No quotas, no preferred logos. Just the right fit for you." },
  { title: "Life-of-Contract Support", desc: "We escalate on your behalf when suppliers underperform." },
];

// Primary navbar (kept lean); secondary links live in the footer.
export const NAV_LINKS = [
  { label: "How We Work", to: "/how-we-work" },
  { label: "Solutions", to: "/solutions" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export const FOOTER_LINKS = [
  { label: "How We Work", to: "/how-we-work" },
  { label: "Solutions", to: "/solutions" },
  { label: "What Changed?", to: "/what-changed" },
  { label: "Billing Breakthrough", to: "/billing-breakthrough" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export const SPEND_TIERS = [
  "Under $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000 - $50,000",
  "$50,000+",
];

export const TECH_INTERESTS = [
  "Connectivity & Internet",
  "UCaaS & CCaaS (CX)",
  "Cybersecurity & Resilience",
  "AI Sourcing & Automation",
  "Managed Service Provider (MSP)",
  "Cloud Sourcing",
  "Data Center Colocation",
];

export const TRIGGER_OPTIONS = [
  "Scaling Fast",
  "Contract Renewal",
  "Inherited Tech Stack",
  "Compliance Deadline",
  "Digital Transformation",
  "Reduce Monthly Costs",
  "Other",
];

export const CASE_STUDIES = [
  {
    slug: "national-healthcare-system",
    industry: "Healthcare",
    title: "Consolidating a fragmented telecom footprint across 40+ facilities",
    client: "National Healthcare System",
    clientRole: "VP of IT",
    summary:
      "A multi-site health system was bleeding budget across dozens of disconnected carrier contracts — with HIPAA exposure hiding in the gaps.",
    challenge:
      "Forty-plus facilities had each signed telecom and connectivity contracts independently over a decade. No one owned the full picture, renewals fired on autopilot, and a looming compliance review demanded a defensible security posture across every site.",
    approach: [
      "Ran a full TEM audit across all 40+ facilities to surface billing errors and redundant circuits",
      "Benchmarked every active contract against the market price floor",
      "Sourced a consolidated SD-WAN + SASE architecture from 9 vetted providers",
      "Negotiated a single master agreement with capped escalators and clean exit ramps",
    ],
    before: [
      { label: "Active carrier contracts", value: "40+" },
      { label: "Annual telecom spend", value: "$4.8M" },
      { label: "Compliance coverage", value: "Partial" },
    ],
    after: [
      { label: "Consolidated agreements", value: "1 master" },
      { label: "Annual telecom spend", value: "$3.5M" },
      { label: "Compliance coverage", value: "Full SOC 2 + HIPAA" },
    ],
    metrics: [
      { value: "27%", label: "Reduction in annual spend" },
      { value: "$1.3M", label: "First-year savings" },
      { value: "6 wks", label: "From audit to signed" },
    ],
    quote:
      "They found leverage in our renewal we didn't know existed — and stayed in the room until it landed. This isn't a broker; it's an extension of my team.",
    tags: ["TEM Audit", "SD-WAN", "Compliance"],
  },
  {
    slug: "financial-services-firm",
    industry: "Financial Services",
    title: "Rebuilding a security stack under a hard SOC 2 deadline",
    client: "Financial Services Firm",
    clientRole: "CIO",
    summary:
      "A fast-growing fintech needed an auditor-ready security stack in 90 days — without overbuying the hyped tooling their vendors were pushing.",
    challenge:
      "An enterprise customer required SOC 2 attestation to close a landmark deal. Incumbent vendors pitched sprawling platforms far beyond the firm's real needs, with pricing anchored to marketing budgets rather than fit.",
    approach: [
      "Mapped required controls directly to the SOC 2 framework",
      "Built an apples-to-apples evaluation matrix across MDR and SASE providers",
      "Eliminated overlapping tools already hiding in the stack",
      "Negotiated ramped pricing tied to actual seat growth",
    ],
    before: [
      { label: "Proposed tooling budget", value: "$1.1M/yr" },
      { label: "Overlapping tools", value: "6" },
      { label: "Audit readiness", value: "0%" },
    ],
    after: [
      { label: "Contracted budget", value: "$800K/yr" },
      { label: "Overlapping tools", value: "0" },
      { label: "Audit readiness", value: "Passed" },
    ],
    metrics: [
      { value: "27%", label: "Cut from proposed budget" },
      { value: "90 days", label: "To audit-ready" },
      { value: "$300K", label: "Annual savings" },
    ],
    quote:
      "For the first time, I had an apples-to-apples view of the market that wasn't written by a vendor. We cut 27% and signed a contract I can actually defend to the board.",
    tags: ["Cybersecurity", "SOC 2", "Negotiation"],
  },
  {
    slug: "multi-location-retail",
    industry: "Multi-location Retail",
    title: "Untangling an inherited stack after a private-equity rollup",
    client: "Multi-location Retail Group",
    clientRole: "CFO",
    summary:
      "A newly-consolidated retail group inherited a tangle of unvetted contracts from six acquired brands — and no one knew what they were paying for.",
    challenge:
      "Post-acquisition, the finance team inherited overlapping SaaS, connectivity, and UCaaS agreements across six brands. Evergreen clauses and phantom charges quietly compounded every month.",
    approach: [
      "Inventoried every active agreement and auto-renewal across all six brands",
      "Flagged evergreen clauses, hidden overages, and duplicate subscriptions",
      "Built a rationalization roadmap sequenced by quick-win savings",
      "Renegotiated the survivors into a unified, benchmarked contract set",
    ],
    before: [
      { label: "Redundant subscriptions", value: "23" },
      { label: "Monthly tech spend", value: "$210K" },
      { label: "Contract visibility", value: "None" },
    ],
    after: [
      { label: "Redundant subscriptions", value: "3" },
      { label: "Monthly tech spend", value: "$146K" },
      { label: "Contract visibility", value: "Single dashboard" },
    ],
    metrics: [
      { value: "31%", label: "Reduction in monthly spend" },
      { value: "$768K", label: "Annualized savings" },
      { value: "1 mo", label: "Audit paid for itself" },
    ],
    quote:
      "The billing audit paid for the entire engagement in the first month. They read the receipts nobody else bothered to.",
    tags: ["Billing Breakthrough", "Contract Rationalization", "UCaaS"],
  },
];

export const RESOURCES = [
  {
    slug: "renewal-negotiation-checklist",
    type: "Checklist",
    title: "The Contract Renewal Negotiation Checklist",
    desc: "The 18 clauses, caps, and exit ramps to pressure-test before you re-sign any multi-year tech contract.",
    pages: "6-page PDF",
    category: "Procurement",
  },
  {
    slug: "telecom-invoice-audit-guide",
    type: "Guide",
    title: "How to Audit Your Own Telecom Invoices",
    desc: "A step-by-step walkthrough for spotting phantom charges, billing errors, and auto-renewed bloat in your monthly statements.",
    pages: "12-page PDF",
    category: "Finance",
  },
  {
    slug: "soc2-readiness-worksheet",
    type: "Worksheet",
    title: "SOC 2 Readiness Worksheet",
    desc: "Map your current controls to the framework and see exactly where the gaps are before an auditor does.",
    pages: "Editable worksheet",
    category: "Compliance",
  },
  {
    slug: "ai-readiness-scorecard",
    type: "Scorecard",
    title: "AI Readiness Scorecard",
    desc: "Score your organization across data, use-cases, and ROI to separate practical AI investment from the hype.",
    pages: "4-page PDF",
    category: "AI",
  },
  {
    slug: "vendor-evaluation-matrix",
    type: "Template",
    title: "The Provider-Agnostic Evaluation Matrix",
    desc: "The same apples-to-apples scoring template we use to compare providers without marketing bias.",
    pages: "Spreadsheet template",
    category: "Procurement",
  },
  {
    slug: "cloud-migration-tco-model",
    type: "Model",
    title: "Cloud Migration TCO Model",
    desc: "Project the true total cost of ownership across migration scenarios before you commit to a platform.",
    pages: "Spreadsheet model",
    category: "Cloud",
  },
];

export const IMAGES = {
  heroNetwork: "https://images.unsplash.com/photo-1532190872407-280735d27e08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5fGVufDB8fHxibGFja3wxNzg1NTA3ODMzfDA&ixlib=rb-4.1.0&q=85",
  heroBokeh: "https://images.unsplash.com/photo-1606778303077-3780ea8d5420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxuZXR3b3JrJTIwdGVjaG5vbG9neXxlbnwwfHx8Ymx1ZXwxNzg1NTA3ODQ2fDA&ixlib=rb-4.1.0&q=85",
  boardroom: "https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzg1MzUxOTYyfDA&ixlib=rb-4.1.0&q=85",
  meeting: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzg1MzUxOTYyfDA&ixlib=rb-4.1.0&q=85",
  dataCenter: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serverBlue: "https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cables: "https://images.pexels.com/photos/5087172/pexels-photo-5087172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};
